import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";

export interface GuideSlotPack {
  id: string;
  name: string;
  guides: number;
  priceGbp: number;
}

export const DEFAULT_GUIDE_SLOT_PACKS: GuideSlotPack[] = [
  { id: "try", name: "Top-up Try", guides: 1, priceGbp: 12 },
  { id: "boost", name: "Top-up Boost", guides: 5, priceGbp: 50 },
  { id: "scale", name: "Top-up Scale", guides: 10, priceGbp: 90 },
];

export function normalizeGuideSlotPack(raw: Record<string, unknown>): GuideSlotPack {
  return {
    id: String(raw["id"] ?? raw["Id"] ?? "")
      .trim()
      .toLowerCase(),
    name: String(raw["name"] ?? raw["Name"] ?? ""),
    guides: Number(raw["guides"] ?? raw["Guides"] ?? 0),
    priceGbp: Number(raw["priceGbp"] ?? raw["PriceGbp"] ?? 0),
  };
}

export function normalizeGuideSlotBalance(raw: Record<string, unknown>): GuideSlotBalance {
  const packSource = (raw["packs"] ?? raw["Packs"] ?? []) as Record<string, unknown>[];
  const packs = packSource
    .map(normalizeGuideSlotPack)
    .filter((pack) => pack.id && pack.guides > 0 && pack.priceGbp > 0);

  const topUpBalance = Number(raw["topUpBalance"] ?? raw["TopUpBalance"] ?? 0);

  return {
    userEmail: String(raw["userEmail"] ?? raw["UserEmail"] ?? ""),
    topUpBalance,
    availableSlots: Number(
      raw["availableSlots"] ?? raw["AvailableSlots"] ?? topUpBalance
    ),
    packs: packs.length ? packs : [...DEFAULT_GUIDE_SLOT_PACKS],
  };
}

export interface GuideSlotBalance {
  userEmail: string;
  topUpBalance: number;
  availableSlots: number;
  packs: GuideSlotPack[];
}

@Injectable({ providedIn: "root" })
export class GuideSlotService {
  private readonly base = environment.apiBaseUrl + "api/GuideSlots";

  constructor(private http: HttpClient) {}

  getBalance(): Observable<GuideSlotBalance> {
    return this.http
      .get<Record<string, unknown>>(`${this.base}/balance`)
      .pipe(map(normalizeGuideSlotBalance));
  }

  purchase(packId: string, paypalOrderId: string): Observable<GuideSlotBalance> {
    return this.http
      .post<Record<string, unknown>>(`${this.base}/purchase`, {
        packId,
        paypalOrderId,
      })
      .pipe(map(normalizeGuideSlotBalance));
  }
}
